'use client'
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart } from '@fortawesome/free-solid-svg-icons';
import Link from 'next/link';
import Image from 'next/image';
import Footer from '../Footer';
import bgimage from '../../components/images/blog_overview.webp';
import notFoundImage from '../../public/images/not_found_image.webp';
import { BlogFilter as Icon_BlogFilter } from "@/components/customer/icons/BlogFilter";
import { BlogSearch as Icon_BlogSearch } from "@/components/customer/icons/BlogSearch";
const SvgIcons = { BlogFilter: Icon_BlogFilter, BlogSearch: Icon_BlogSearch };
import { getImageUrl } from "@/lib/imageUrl";
import { sanitizeHtml } from "@/lib/sanitize";

interface BlogCategory {
  id: number;
  name: string;
  image?: string;
  content?: string;
}

interface Blog {
  slug: string;
  title: string;
  image: string;
  updated_at: string;
  short_content: string;
  categories: string | number[] | null;
}
interface Blog { slug: string; title: string; image: string; updated_at: string; short_content: string; }
interface BlogCategory { id: number; name: string; image?: string; content?: string; }

interface Props {
  initialBlogs: Blog[];
  categories: BlogCategory[];
}
export default function BlogOverview({ initialBlogs, categories }: Props) {

  const [blogs, setBlogs] = useState<Blog[]>(initialBlogs);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const blogsPerPage = 30;
  const buttonRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const blogSectionRef = useRef<HTMLElement>(null);

  const [activeSlide, setActiveSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [autoSlideKey, setAutoSlideKey] = useState(0);
  const [imageLoadedMap, setImageLoadedMap] = useState<{ [slug: string]: boolean }>({});

  // Handle click outside for mobile filter popup
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setBlogs(initialBlogs);
  }, [initialBlogs]);

  // Deep-link support: /blog?page=N (the crawlable pagination anchors) sets the initial page.
  useEffect(() => {
    const p = Number(new URLSearchParams(window.location.search).get('page'));
    if (Number.isFinite(p) && p >= 1) setCurrentPage(p);
  }, []);

  // Auto-slide for slider
  useEffect(() => {
    if (!isPaused && categories.length > 0) {
      const interval = setInterval(() => {
        setActiveSlide((prev) => (prev + 1) % categories.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [isPaused, categories.length]);




  // Get category name by ID
  const getCategoryName = (categoryData: string | number[] | null): string => {
    if (!categoryData) return 'Business';
    try {
      const parsedCategories = typeof categoryData === 'string' ? JSON.parse(categoryData) : categoryData;
      if (Array.isArray(parsedCategories) && parsedCategories.length > 0) {
        const categoryId = parseInt(String(parsedCategories[0]), 10);
        const category = categories.find((cat) => cat.id === categoryId);
        return category ? category.name : 'Business';
      }
      return 'Business';
    } catch (error) {
      console.error('Error parsing category data:', error);
      return 'Business';
    }
  };

  // Filter blogs based on category and search query
  const filteredBlogs = blogs.filter((blog) => {
    const query = searchQuery.trim().toLowerCase();

    // Check if search query matches blog title or category id
    const titleMatch = blog.title.toLowerCase().includes(query);
    let categoryIds: number[] = [];
    if (typeof blog.categories === 'string') {
      try {
        categoryIds = JSON.parse(blog.categories).map(Number).filter((id) => !isNaN(id));
      } catch {
        categoryIds = [];
      }
    } else if (Array.isArray(blog.categories)) {
      categoryIds = blog.categories.map(Number).filter((id) => !isNaN(id));
    }
    const idMatch = query && !isNaN(Number(query)) ? categoryIds.includes(Number(query)) : false;

    // Category filter
    const matchesCategory = selectedCategory === null || categoryIds.includes(selectedCategory);

    return (query === '' ? true : titleMatch || idMatch) && matchesCategory;
  });

  // Pagination logic

  const totalPages = Math.ceil(filteredBlogs.length / blogsPerPage);
  const startIndex = (currentPage - 1) * blogsPerPage;
  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages;


  // NEW - Better reset function
  const resetPagination = useCallback(() => {
    setCurrentPage(1);
    if (blogSectionRef.current) {
      blogSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  // Reset currentPage when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, searchQuery]);

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleButtonClick = (index: number) => {
    setActiveSlide(index);
    setAutoSlideKey((prev) => prev + 1);
  };

  const handlePausePlay = () => {
    setIsPaused((prev) => !prev);
  };

  const handleReadBlogClick = (categoryId: number) => {
    setSelectedCategory(categoryId);
    setActiveSlide(categories.findIndex((cat) => cat.id === categoryId));
    setAutoSlideKey((prev) => prev + 1);
    if (blogSectionRef.current) {
      blogSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };
  useEffect(() => {
    if (blogSectionRef.current) {
      blogSectionRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [currentPage]);

  return (
    <>

      <div className="z-10 bg-white">
        {/* Single page-level H1 for SEO/a11y — the hero is a rotating category
            carousel (each an h2), so the page title lives here. */}
        <h1 className="sr-only">Vilva Business Blog — Business Growth, Tech &amp; Marketing Insights</h1>
        <div className="w-full relative">
          {/* Slider */}
          <div className="overflow-hidden relative w-full">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${activeSlide * 100}%)` }}
            >
              {categories.map((cat, i) => (
                <div
                  key={cat.id}
                  className="relative min-w-full h-[560px] flex flex-col md:items-center items-start justify-center p-4 md:p-8 text-center"
                >
                  {/* ✅ LCP Image - moved INSIDE the div body */}
                  <Image
                    src={
                      cat.image
                        ? getImageUrl(`/${cat.image}`)
                        : bgimage.src
                    }
                    alt={cat.name ?? 'Blog hero'}
                    fill
                    priority={i === 0}
                    sizes="100vw"
                    referrerPolicy="no-referrer"
                    className="object-cover object-center"
                  />

                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black/40 z-0" />

                  {/* Content */}
                  <div className="relative z-10">
                    <h2 className="text-white text-left text-[40px] md:text-[52px] font-[600] leading-[100%] font-Schibsted_Grotesk">
                      {cat.name}
                    </h2>
                    {cat.content && (
                      <p
                        className="text-white mt-4 md:text-center text-start text-[18px] font-normal leading-[160%] font-Schibsted_Grotesk"
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(cat.content) }}
                      />
                    )}
                    <button
                      onClick={() => handleReadBlogClick(cat.id)}
                      className="text-[var(--Main-colour,#195AFF)] focus:outline-none mt-4 flex items-center gap-2 text-[16px] font-normal leading-none mx-auto font-Schibsted_Grotesk bg-white rounded-[8px] px-6 py-3"
                    >
                      Read Blog
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path
                          d="M16.6666 10H3.33331"
                          stroke="#195AFF"
                          strokeWidth="1.5"
                          strokeMiterlimit="10"
                          strokeLinecap="round"
                        />
                        <path
                          d="M10.8063 16.3838L16.2837 10.9065C16.4033 10.7878 16.4983 10.6467 16.5631 10.491C16.628 10.3354 16.6613 10.1686 16.6613 10C16.6613 9.83141 16.628 9.66458 16.5631 9.509C16.4983 9.35333 16.4033 9.21216 16.2837 9.09358L10.8063 3.61618"
                          stroke="#195AFF"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}

            </div>
            {/* Navigation Buttons */}
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex md:hidden mx-auto z-30 items-center gap-3">
              <div className="flex justify-center gap-2">
                {categories.map((cat, idx) => (
                  <button
                    key={cat.id}
                    aria-label={`Go to slide ${idx + 1}`}
                    className={`transition-all focus:outline-none duration-300 ease-in-out ${activeSlide === idx
                      ? 'h-[6px] w-[40px] bg-white rounded-[3px]'
                      : 'h-[6px] w-[40px] rounded-[3px] bg-[rgba(255,255,255,0.10)] backdrop-blur-[2px]'
                      }`}
                    onClick={() => handleButtonClick(idx)}
                  ></button>
                ))}
              </div>
              <button onClick={handlePausePlay}
                aria-label="pause button">
                {isPaused ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                    fill="none"
                  >
                    <path
                      d="M12 24V8L24 16L12 24Z"
                      stroke="white"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M20.3333 3.66669H11.6666C7.24835 3.66669 3.66663 7.24841 3.66663 11.6667V20.3334C3.66663 24.7516 7.24835 28.3334 11.6666 28.3334H20.3333C24.7516 28.3334 28.3333 24.7516 28.3333 20.3334V11.6667C28.3333 7.24841 24.7516 3.66669 20.3333 3.66669Z"
                      stroke="white"
                      strokeWidth="0.8"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                    fill="none"
                  >
                    <path
                      d="M13.4321 10.2969H10.9635C10.5953 10.2969 10.2969 10.5953 10.2969 10.9635V21.0361C10.2969 21.4043 10.5953 21.7028 10.9635 21.7028H13.4321C13.8004 21.7028 14.0988 21.4043 14.0988 21.0361V10.9635C14.0988 10.5953 13.8004 10.2969 13.4321 10.2969Z"
                      stroke="white"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M21.0363 10.2969H18.5677C18.1994 10.2969 17.901 10.5953 17.901 10.9635V21.0361C17.901 21.4043 18.1994 21.7028 18.5677 21.7028H21.0363C21.4045 21.7028 21.703 21.4043 21.703 21.0361V10.9635C21.703 10.5953 21.4045 10.2969 21.0363 10.2969Z"
                      stroke="white"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M20.3333 3.66669H11.6666C7.24835 3.66669 3.66663 7.24841 3.66663 11.6667V20.3334C3.66663 24.7516 7.24835 28.3334 11.6666 28.3334H20.3333C24.7516 28.3334 28.3333 24.7516 28.3333 20.3334V11.6667C28.3333 7.24841 24.7516 3.66669 20.3333 3.66669Z"
                      stroke="white"
                      strokeWidth="0.8"
                    />
                  </svg>
                )}
              </button>
            </div>
            <div className="absolute bottom-2 hidden md:flex left-1/2 -translate-x-1/2 z-30 items-end gap-3">
              <div className="flex justify-center rounded-t-[8px] bg-[rgba(255,255,255,0.10)] backdrop-blur-[2px] gap-2">
                {categories.map((cat, idx) => (
                  <div
                    key={cat.id}
                    className={` cursor-pointer text-white border-b-[2px] border-transparent focus:outline-none whitespace-nowrap text-[18px] font-normal leading-[100%] font-Schibsted_Grotesk p-3 transition-all duration-300 ease-in-out ${activeSlide === idx ? 'border-b-[2px] border-white text-[#090D28]' : ''
                      }`}
                    onClick={() => handleButtonClick(idx)}
                  >
                    {cat.name}
                  </div>
                ))}
              </div>
              <button onClick={handlePausePlay}
                aria-label='play button'>
                {isPaused ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                    fill="none"
                  >
                    <path
                      d="M12 24V8L24 16L12 24Z"
                      stroke="white"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M20.3333 3.66669H11.6666C7.24835 3.66669 3.66663 7.24841 3.66663 11.6667V20.3334C3.66663 24.7516 7.24835 28.3334 11.6666 28.3334H20.3333C24.7516 28.3334 28.3333 24.7516 28.3333 20.3334V11.6667C28.3333 7.24841 24.7516 3.66669 20.3333 3.66669Z"
                      stroke="white"
                      strokeWidth="0.8"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                    fill="none"
                  >
                    <path
                      d="M13.4321 10.2969H10.9635C10.5953 10.2969 10.2969 10.5953 10.2969 10.9635V21.0361C10.2969 21.4043 10.5953 21.7028 10.9635 21.7028H13.4321C13.8004 21.7028 14.0988 21.4043 14.0988 21.0361V10.9635C14.0988 10.5953 13.8004 10.2969 13.4321 10.2969Z"
                      stroke="white"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M21.0363 10.2969H18.5677C18.1994 10.2969 17.901 10.5953 17.901 10.9635V21.0361C17.901 21.4043 18.1994 21.7028 18.5677 21.7028H21.0363C21.4045 21.7028 21.703 21.4043 21.703 21.0361V10.9635C21.703 10.5953 21.4045 10.2969 21.0363 10.2969Z"
                      stroke="white"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M20.3333 3.66669H11.6666C7.24835 3.66669 3.66663 7.24841 3.66663 11.6667V20.3334C3.66663 24.7516 7.24835 28.3334 11.6666 28.3334H20.3333C24.7516 28.3334 28.3333 24.7516 28.3333 20.3334V11.6667C28.3333 7.24841 24.7516 3.66669 20.3333 3.66669Z"
                      stroke="white"
                      strokeWidth="0.8"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        <section ref={blogSectionRef} className="container mx-auto px-4 py-8">
          <div className="text-[#70708D] flex items-center mb-20 font-Schibsted_Grotesk text-sm font-normal leading-tight">
            <Link href="/" className="text-[#70708D]">
              Home
            </Link>
            {' > '}
            Blog
          </div>
          <h2 className="text-[#090D28] mb-4 text-left font-Schibsted_Grotesk text-3xl font-[700] leading-[120%]">
            {selectedCategory ? getCategoryName([selectedCategory]) : 'All Blogs'}
          </h2>
          <p className="text-[#5B6473] mb-11 max-w-3xl text-left font-Schibsted_Grotesk text-[15px] leading-[150%]">
            The Vilva Business blog covers what actually works for growing a business online in
            India — ecommerce platform comparisons, WhatsApp Business API setup, technical SEO
            fixes, and digital marketing tactics, plus real case studies from Indian brands using
            Vilva D2C, GrowSuite, and Vilva Chat. Filter by category below, or use search to find a
            specific topic.
          </p>

          {/* Desktop View Filter Buttons */}
          <div className="hidden flex-wrap gap-3 md:flex justify-between items-center mb-10">
            <div className="rounded-xl w-fit overflow-hidden flex justify-around items-center border border-[#E4EBFB] bg-[#F6F8FE]">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`text-[#000] font-Schibsted_Grotesk focus:outline-none text-base whitespace-nowrap font-[500] flex items-center justify-center gap-2 px-3 py-3 ${selectedCategory === null ? 'text-[#195AFF]' : 'hover:bg-blue-200'
                  }`}
              >
                All Blogs
              </button>
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`text-[#000] font-Schibsted_Grotesk focus:outline-none  text-base whitespace-nowrap font-[500]  flex items-center justify-center gap-2 px-3 py-3 ${selectedCategory === category.id ? 'text-[#195AFF]' : 'hover:bg-blue-200'
                    }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1">
              <div className="relative w-full">
                <input
                  type="text"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="border-none h-[48px] px-9 rounded-[8px] bg-[#E4EBFB] font-Schibsted_Grotesk"
                />
                <svg
                  className="absolute left-2 top-3"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M10.7828 18.8276C12.3741 18.8298 13.9302 18.3601 15.2544 17.4781C16.5785 16.596 17.6112 15.3413 18.2216 13.8726C18.832 12.4039 18.9929 10.7872 18.6837 9.2271C18.3746 7.66702 17.6093 6.23364 16.4849 5.10831C15.3604 3.98299 13.9272 3.2163 12.3666 2.90525C10.8061 2.5942 9.18823 2.75277 7.71786 3.3609C6.24748 3.96902 4.99062 4.99937 4.10632 6.32158C3.22202 7.64379 2.75 9.19844 2.75 10.7888C2.75 12.919 3.59596 14.9621 5.10209 16.4693C6.60821 17.9766 8.65135 18.8248 10.7828 18.8276Z"
                    stroke="black"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M16.4883 16.491L21.25 21.25"
                    stroke="black"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <button
                onClick={() => setSearchQuery(searchQuery)}
                className="rounded-[8px] text-white text-[16px] font-[500] leading-[120%] font-Schibsted_Grotesk bg-[var(--Main-colour,#195AFF)] flex h-12 px-8 py-4 justify-center items-center"
              >
                Search
              </button>
            </div>
          </div>

          {/* Mobile View Filter Buttons */}
          <div className="flex md:hidden relative justify-between gap-1 items-center mb-10">
            <div
              ref={buttonRef}
              onClick={() => setIsOpen(!isOpen)}
              className="inline-flex h-12 px-3 py-2 justify-center items-center gap-[0px] rounded-[8px] bg-[#E4EBFB] shrink-0"
            >
              <SvgIcons.BlogFilter />
              {isOpen && (
                <>
                  <div className="fixed inset-0 bg-black bg-opacity-50 z-40" />
                  <div
                    ref={popupRef}
                    className="absolute left-0 mt-2 w-[200px] bg-white rounded-[8px] p-[10px_12px] flex flex-col justify-center items-center gap-[10px] z-50"
                    style={{ top: 'calc(100% + 8px)' }}
                  >
                    <div
                      onClick={() => {
                        setSelectedCategory(null);
                        setIsOpen(false);
                      }}
                      className={`w-full text-[#000] text-[16px] font-[500] leading-none font-Schibsted_Grotesk text-center py-2 rounded-[8px] ${selectedCategory === null ? 'bg-[#195AFF] text-white' : 'hover:bg-gray-100'
                        } cursor-pointer`}
                    >
                      All Blogs
                    </div>
                    {categories.map((category) => (
                      <div
                        key={category.id}
                        onClick={() => {
                          setSelectedCategory(category.id);
                          setIsOpen(false);
                        }}
                        className={`w-full text-[#000] text-[16px] font-[500] leading-none font-Schibsted_Grotesk text-center py-2 rounded-[8px] ${selectedCategory === category.id ? 'bg-[#195AFF] text-white' : 'hover:bg-gray-100'
                          } cursor-pointer`}
                      >
                        {category.name}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="flex w-full items-center gap-1">
              <div className="relative w-full">
                <input
                  type="text"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="border-none h-[48px] w-full px-9 rounded-[8px] bg-[#E4EBFB] font-Schibsted_Grotesk"
                />
                <svg
                  className="absolute left-2 top-3"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M10.7828 18.8276C12.3741 18.8298 13.9302 18.3601 15.2544 17.4781C16.5785 16.596 17.6112 15.3413 18.2216 13.8726C18.832 12.4039 18.9929 10.7872 18.6837 9.2271C18.3746 7.66702 17.6093 6.23364 16.4849 5.10831C15.3604 3.98299 13.9272 3.2163 12.3666 2.90525C10.8061 2.5942 9.18823 2.75277 7.71786 3.3609C6.24748 3.96902 4.99062 4.99937 4.10632 6.32158C3.22202 7.64379 2.75 9.19844 2.75 10.7888C2.75 12.919 3.59596 14.9621 5.10209 16.4693C6.60821 17.9766 8.65135 18.8248 10.7828 18.8276Z"
                    stroke="black"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M16.4883 16.491L21.25 21.25"
                    stroke="black"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <button
                onClick={() => setSearchQuery(searchQuery)}
                aria-label="View all testimonials"
                className="rounded-[8px] text-white text-[18px] font-[600] leading-[120%] font-Schibsted_Grotesk bg-[var(--Main-colour,#195AFF)] flex h-12 px-4 py-4 justify-center items-center"
              >
                <SvgIcons.BlogSearch />
              </button>
            </div>
          </div>






          {/* Blog Cards */}
          {initialBlogs.length > 0 ? (
            <div className="grid md:grid-cols-2 gap-12 lg:grid-cols-3 bg-white">
              {error && <p className="text-red-500">Error: {error}</p>}
              {filteredBlogs.map((blog: Blog, blogIndex: number) => {
                // Every blog is rendered (and stays in the server HTML / view-source, so all
                // ~90 posts are crawlable), but only the current page's slice is visible —
                // `hidden` (display:none) removes it from layout and skips its lazy image load.
                const isOnCurrentPage = blogIndex >= startIndex && blogIndex < startIndex + blogsPerPage;
                // Parse categories to get the first category ID
                let firstCategoryId: number | null = null;
                if (blog.categories) {
                  try {
                    const parsedCategories =
                      typeof blog.categories === 'string'
                        ? JSON.parse(blog.categories).map(Number)
                        : blog.categories;
                    if (Array.isArray(parsedCategories) && parsedCategories.length > 0) {
                      firstCategoryId = parsedCategories[0];
                    }
                  } catch (error) {
                    console.error('Error parsing categories for blog:', blog.slug, error);
                  }
                }

                // Find the category name and convert to URL-friendly slug
                let categorySlug = '';
                if (firstCategoryId !== null) {
                  const category = categories.find((cat) => cat.id === firstCategoryId);
                  categorySlug = category
                    ? category.name
                      .toLowerCase()
                      .trim()
                      .replace(/[^a-z0-9]+/g, '-') // Convert to slug format
                      .replace(/(^-|-$)/g, '') // Remove leading/trailing hyphens
                    : '';
                }

                // Construct the SEO-friendly URL
                const blogUrl = `/blog/${blog.slug}`;

                return (
                  <Link href={blogUrl} key={blog.slug} className={isOnCurrentPage ? undefined : 'hidden'}>
                    <div className="bg-[#F6F8FE] relative rounded-[8px] group h-full hover:bg-gray-100 transition-colors duration-300 ease-in-out">
                      <div className="overflow-hidden rounded-t-[12px]">
                        {/* Skeleton sits UNDER the image (-z-10 on an absolute box) rather than
                            over it. It previously had z-0 while the img had z-10, but z-index is
                            inert on a statically-positioned element, so the absolute skeleton
                            painted on top and any failure to clear it hid the thumbnail entirely. */}
                        {!imageLoadedMap[blog.slug] && (
                          <div className="absolute inset-0 w-full h-[300px] bg-gray-200 animate-pulse -z-10" />
                        )}
                        <div className="bg-white relative">
                          <img
                            src={
                              blog.image
                                ? getImageUrl(`/${blog.image}`)
                                : notFoundImage.src
                            }
                            alt={blog.title}
                            width={400}
                            height={300}
                            loading="lazy"
                            referrerPolicy="no-referrer"
                            className="w-full bg-white h-[300px] object-cover transform scale-100 group-hover:scale-105 transition-transform duration-300 ease-in-out relative z-10"
                            // onLoad is a React handler attached during hydration. On SSR'd markup
                            // the browser often finishes decoding first, and React does not replay
                            // the native load event — so this never fired and the skeleton stayed
                            // up forever. Seeding from img.complete on mount closes that gap.
                            ref={(node) => {
                              if (node?.complete) {
                                setImageLoadedMap((prev) =>
                                  prev[blog.slug] ? prev : { ...prev, [blog.slug]: true }
                                );
                              }
                            }}
                            onLoad={() =>
                              setImageLoadedMap((prev) => ({
                                ...prev,
                                [blog.slug]: true,
                              }))
                            }
                            onError={() =>
                              setImageLoadedMap((prev) => ({
                                ...prev,
                                [blog.slug]: true,
                              }))
                            }
                          />
                        </div>
                      </div>
                      <div className="px-4 py-6 flex flex-col justify-between  sm:h-[300px] h-auto">
                        <div className=''>
                          <span className="text-[#195AFF] text-[14px] font-[400] leading-[120%] font-Schibsted_Grotesk">
                            {`${getCategoryName(blog.categories)} | ${new Date(blog.updated_at).toLocaleDateString('en-GB', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric',
                            })}`}
                          </span>
                          <div className="mt-2">
                            <h3 className="text-[#090D28] font-Schibsted_Grotesk my-4 text-[20px] font-[600] leading-[130%]">
                              {blog.title}
                            </h3>
                            <div
                              className="blog-content line-clamp-4"
                              dangerouslySetInnerHTML={{ __html: sanitizeHtml(blog.short_content) }}
                            />
                          </div>
                        </div>
                        <div>
                          <span
                            aria-label={`Learn more about ${blog.title}`}
                            className="text-[#195AFF] flex whitespace-nowrap w-fit mt-[6px] items-center gap-2 font-Schibsted_Grotesk text-[16px] font-[600] leading-none"
                          >
                            <span className="md:opacity-0 md:w-0 w-full group-hover:opacity-100 group-hover:w-full transition-all duration-300 ease-in-out font-Schibsted_Grotesk">
                              Learn More
                            </span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="18"
                              height="18"
                              viewBox="0 0 20 20"
                              fill="none"
                              className="opacity-100 shrink-0"
                            >
                              <path
                                d="M16.6667 10H3.33334"
                                stroke="#195AFF"
                                strokeWidth="1.5"
                                strokeMiterlimit="10"
                                strokeLinecap="round"
                              />
                              <path
                                d="M10.8063 16.3839L16.2837 10.9065C16.4033 10.7879 16.4983 10.6467 16.5631 10.491C16.628 10.3354 16.6613 10.1686 16.6613 10C16.6613 9.8314 16.628 9.66461 16.5631 9.50903C16.4983 9.35336 16.4033 9.21219 16.2837 9.09361L10.8063 3.61621"
                                stroke="#195AFF"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="w-full text-center pb-16 text-gray-500 text-lg font-Schibsted_Grotesk">
              <Image width={300} className="mx-auto" src={notFoundImage} alt="notFoundImage" />
              No blogs found.
            </div>
          )}

          {/* Pagination */}
          <div className="flex space-x-1 mt-8 justify-center items-center">
            <button
              onClick={handlePrevious}
              aria-label="Previous page"
              disabled={currentPage === 1}
              className={`rounded-[8px] w-[44px] h-[44px] border border-[#E4E9F0] bg-white shadow-[0px_8px_16px_rgba(0,9,56,0.02)] flex justify-center items-center ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''
                }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-chevron-left-icon lucide-chevron-left"><path d="m15 18-6-6 6-6" /></svg>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <a
                key={page}
                href={`/blog?page=${page}`}
                aria-label={`Go to page ${page}`}
                aria-current={currentPage === page ? 'page' : undefined}
                onClick={(e) => { e.preventDefault(); setCurrentPage(page); }}
                className={`cursor-pointer rounded-[8px] w-[44px] h-[44px] border border-[#E4E9F0] shadow-[0px_8px_16px_rgba(0,9,56,0.02)] flex items-center justify-center text-[18px] font-medium font-Schibsted_Grotesk ${currentPage === page ? 'bg-[#195AFF] text-white' : 'text-[#70708D] hover:bg-gray-100'
                  }`}
              >
                {page}
              </a>
            ))}
            <button
              onClick={handleNext}
              aria-label="Next page"
              disabled={currentPage === totalPages}
              className={`rounded-[8px] w-[44px] h-[44px] border border-[#E4E9F0] bg-white shadow-[0px_8px_16px_rgba(0,9,56,0.02)] flex justify-center items-center ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''
                }`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-chevron-right-icon lucide-chevron-right"><path d="m9 18 6-6-6-6" /></svg>
            </button>
          </div>
        </section>
      </div>
    </>
  );
};

